import * as styles from "./Ripple.css";

import { waapi } from "animejs";
import type { WAAPIAnimation } from "animejs";
import { clsx } from "clsx";
import type { JSX } from "react";
import { useEffect, useRef } from "react";

import type { RippleProps } from "./Ripple.types";

const RIPPLE_DURATION_MS = 2000;
const RIPPLE_OPACITY = 0.3;
const RIPPLE_FADE_IN_MS = 150;
const RIPPLE_FADE_OUT_MS = 400;
const RIPPLE_CENTER_OVERSHOOT = 1.4;

const toLength = (value: number | string): string =>
  typeof value === "number" ? `${value}px` : value;

type ActiveRipple = {
  element: HTMLSpanElement;
  expansion: WAAPIAnimation;
};

export function Ripple({
  color,
  duration = RIPPLE_DURATION_MS,
  shape = "inherit",
  spread,
  center = false,
  className,
}: RippleProps): JSX.Element {
  const layerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    const target = layer?.parentElement;

    if (!layer || !target) {
      return;
    }

    const activeRipples: ActiveRipple[] = [];
    const animations = new Set<WAAPIAnimation>();

    function handlePointerDown(event: PointerEvent) {
      if (!layer) {
        return;
      }

      const rect = layer.getBoundingClientRect();
      const extent = Math.max(rect.width, rect.height);
      const size = extent * (center ? RIPPLE_CENTER_OVERSHOOT : 2);
      const originX = center ? rect.width / 2 : event.clientX - rect.left;
      const originY = center ? rect.height / 2 : event.clientY - rect.top;

      const ripple = document.createElement("span");
      ripple.className = styles.ripple;
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${originX - size / 2}px`;
      ripple.style.top = `${originY - size / 2}px`;

      if (color) {
        ripple.style.backgroundColor = color;
      }

      layer.appendChild(ripple);

      const expansion = waapi.animate(ripple, {
        transform: ["scale(0)", "scale(1)"],
        opacity: {
          from: 0,
          to: RIPPLE_OPACITY,
          duration: RIPPLE_FADE_IN_MS,
        },
        duration,
        ease: "outExpo",
      });

      animations.add(expansion);
      activeRipples.push({ element: ripple, expansion });
    }

    function releaseRipples() {
      activeRipples.forEach(({ element, expansion }) => {
        const fadeOut = waapi.animate(element, {
          opacity: 0,
          duration: RIPPLE_FADE_OUT_MS,
          ease: "outQuad",
          onComplete: (self) => {
            animations.delete(self);
            animations.delete(expansion);
            expansion.cancel();
            element.remove();
          },
        });

        animations.add(fadeOut);
      });

      activeRipples.length = 0;
    }

    target.addEventListener("pointerdown", handlePointerDown);
    target.addEventListener("pointerup", releaseRipples);
    target.addEventListener("pointerleave", releaseRipples);
    target.addEventListener("pointercancel", releaseRipples);

    return () => {
      target.removeEventListener("pointerdown", handlePointerDown);
      target.removeEventListener("pointerup", releaseRipples);
      target.removeEventListener("pointerleave", releaseRipples);
      target.removeEventListener("pointercancel", releaseRipples);
      animations.forEach((animation) => animation.cancel());
      animations.clear();
    };
  }, [color, duration, center]);

  return (
    <span
      ref={layerRef}
      aria-hidden
      className={clsx(styles.layer, className)}
      style={{
        inset:
          spread === undefined ? undefined : `calc(-1 * ${toLength(spread)})`,
        borderRadius: shape === "circle" ? "50%" : undefined,
        overflow: center ? "visible" : undefined,
      }}
    />
  );
}
